import { getDeviceMode } from '../../VNMode/Selectors'
import { setRoute } from '../../VNRoutes/ActionCreators'
import { cardReaderApproved } from '../../utils/orderUtils'
import { addPaymentToOrderInProgress, setOrderInProgress, startOfflineCardOrder, startOnlineCardOrder } from '../../actions/order'
import { isOfflineApproval } from '../../utils/offlineReplayStatuses'
import { getOrder } from '../../selectors/order'
import { PAYMENT_TYPES } from '../../utils/paymentTypes'
import createDataDogLog from '../../utils/datadog'
import { INTERNAL_ORDER_STATES, isTabbed } from '../../utils/orderStates'
import { MODES } from '../../VNMode/Reducer'
import {
  getAttendantId,
  getBalanceDueInCents,
  getHasTipApplied,
  getIsAuthorization,
  getLineItems,
  getOrderNumber,
  getOrderUUID,
  getPayments,
  getSignature,
  getTenderAmountInCents,
  getTipAmountCents,
} from '../Selectors'
import { setActiveAttendantId } from '../../actions/attendant'
import { createConfigSucceeded } from '@ordernext/networking-stadium/stadium/actions'
import { setRecoveryData } from '../ActionCreators'
import { getPaymentId } from '../Selectors'
import { setCartFromRecovery } from '../../actions/cart'
import { stringToCents } from '../../utils/formatters'
import { setIsTabNameRequired } from '../../VNTabs/ActionCreators'

/**
 * Sets last transaction information in CFD
 * Before doing anything with this information, in order to send it back to L1400, the
 * devices need to re-establish connectivity.
 *
 * @param {Function} dispatch
 * @param {Object} data - cardReaderData
 */
const recoverCFDTransaction = (dispatch, data) => {
  // set transaction data in state for later use
}

/**
 * Resumes Card Reader 'handleResult'
 *   - On Success, addPayment to order OR start online/offline card order
 *   - On Failure, startOnline as Failed, set error messages
 *   - Redirect user to fallback routes
 *
 * @param {Function} dispatch
 * @param {Object} state
 * @param {Object} data - cardReaderData
 */
const recoverPosModeTransaction = (dispatch, state, data) => {
  // need to fetch these from state (set at card reader)
  const orderUuid = getOrderUUID(state)
  const orderNumber = getOrderNumber(state)
  const paymentId = getPaymentId(state)
  const attendantId = getAttendantId(state)
  const signature = getSignature(state)
  const tipAmountInCents = getTipAmountCents(state)
  const tenderAmountInCents = getTenderAmountInCents(state) || stringToCents(data?.amount)
  const isAuthorization = getIsAuthorization(state)
  const lineItems = getLineItems(state)
  const payments = getPayments(state)

  // recompose order in progress with latest state
  const orderInProgress = {
    ...getOrder(state, orderUuid),
    balanceDueInCents: getBalanceDueInCents(state),
    hasTipApplied: getHasTipApplied(state),
  }

  if (payments.length) {
    orderInProgress.payments = payments
  }
  const isTabOrder = isTabbed(orderInProgress)

  // Re-Login by establishing currently previously in "attendant" and cart
  dispatch(setActiveAttendantId(attendantId))
  dispatch(createConfigSucceeded({}))
  dispatch(setOrderInProgress(orderInProgress))
  dispatch(setCartFromRecovery({ lineItems }))

  // Log occurrence to DataDog
  createDataDogLog('info', 'VN Transcation Recovery - WIN Death Occurred:', {
    data,
    uuid: orderUuid,
    paymentId,
    invoiceNumber: data?.invoiceNumber,
  })

  // TODO: add support for split tenders
  // when we come back, it's not taking into account the actual amount charged
  // we get pushed to the pin screen...

  // Card Reader Aborted, Error, Canceled and Declines
  if (!cardReaderApproved(data)) {
    if (!isAuthorization && !isTabOrder) {
      dispatch(
        startOnlineCardOrder({
          cardReaderData: data,
          signature,
          tipAmountInCents,
          orderNumber,
          orderState: INTERNAL_ORDER_STATES.FAILED,
        }),
      )
    }

    createDataDogLog('error', 'Card Reader - Card Reader Error', {
      data,
      uuid: orderUuid,
      paymentId,
      invoiceNumber: data?.invoiceNumber,
    })

    const message = [data?.reasonCode ?? '', data?.reasonText ?? '', data?.message ?? ''].join(', ')

    dispatch(setRoute('/card-reader', { recoveryStatus: 'error', message }))
    return
  }

  // Adding Payment to a Tabbed Order
  if (paymentId) {
    dispatch(
      addPaymentToOrderInProgress({
        cardReaderData: { ...data, orderNumber, uuid: orderUuid },
        signature,
        orderNumber,
        tipAmountInCents,
        tenderAmountInCents,
        paymentId,
        paymentType: PAYMENT_TYPES.CREDIT_CARD,
        navigateHistory: true,
        isApprovedOffline: isOfflineApproval({ cardReaderData: data }),
      }),
    )
    return
  }

  // Starting "auth" (Tab or Backbar)
  if (isAuthorization) {
    dispatch(setIsTabNameRequired(true))
    dispatch(
      setRoute('/concession-order', {
        cardReaderData: data,
        paymentId,
        paymentType: PAYMENT_TYPES.CREDIT_CARD,
        orderNumber,
        isTabOrder,
      }),
    )
    return
  }

  const payload = { cardReaderData: data, signature, tipAmountInCents, tenderAmountInCents, orderNumber, lineItems }

  // Offline
  if (isOfflineApproval({ cardReaderData: data })) {
    dispatch(startOfflineCardOrder(payload))
    dispatch(setRoute(`receipt/${orderUuid}`))
    return
  }

  // Default Online "sale"
  dispatch(startOnlineCardOrder(payload))
  dispatch(setRoute(`receipt/${orderUuid}`))
  return
}

/**
 * Transaction Recovery Actions
 *  - CFD will get this information set to its state till it confirms that
 *    it's connected to the L1400.  On success, CFD sends this information
 *    to L1400, on failure, we need to reset the modals on both devices
 *    then retry payment (maybe with a different card)
 *  - POS will attempt to resume transaction by checking if we are
 *    sucessful, offline, tabbed or failed.
 */
export const VNTransactionRecovery = (dispatch, getState, data) => {
  const state = getState()
  const cfdMode = getDeviceMode(state)

  dispatch(setRecoveryData(data))

  if (cfdMode === MODES.CFD) {
    recoverCFDTransaction(dispatch, data)
    return
  }

  recoverPosModeTransaction(dispatch, state, data)
}

export default VNTransactionRecovery
